import { useState, type MouseEvent } from 'react';
import { Box, CircularProgress, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import {
  getPlaylistNextShowModeDisplay,
  getPlaylistNextShowModeIconColor,
  PLAYLIST_NEXT_SHOW_MODE_DISPLAY,
} from '@/helpers/media-playlist-next-show-mode.helper';
import { PLAYLIST_NEXT_SHOW_MODES, type PlaylistNextShowMode } from '@/types/dtos/media/playlist-next-show-mode.enum';
import { PlaylistModeIcon } from '@/components/events/detail/tabs/common/media/playlist-mode-icon';

type PlaylistNextShowModeMenuProps = {
  value: PlaylistNextShowMode;
  disabled?: boolean;
  loading?: boolean;
  testId?: string;
  onSelect: (mode: PlaylistNextShowMode) => void;
};

const triggerIconSx = {
  'padding': '2px',
  'display': 'inline-flex',
  'alignItems': 'center',
  'justifyContent': 'center',
  '& > svg': {
    fontSize: '20px',
    width: '20px',
    height: '20px',
  },
} as const;

export function PlaylistNextShowModeMenu({
  value,
  disabled = false,
  loading = false,
  testId,
  onSelect,
}: PlaylistNextShowModeMenuProps) {
  const t = useTranslations();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const currentDisplay = getPlaylistNextShowModeDisplay(value);
  const currentColor = getPlaylistNextShowModeIconColor(value);

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (disabled || loading) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (event: MouseEvent<HTMLLIElement>, mode: PlaylistNextShowMode) => {
    event.stopPropagation();
    handleClose();
    if (mode !== value) {
      onSelect(mode);
    }
  };

  return (
    <>
      <Tooltip title={t(currentDisplay.tooltipKey)} disableInteractive>
        <span>
          <IconButton
            size="small"
            aria-label={t(currentDisplay.tooltipKey)}
            aria-haspopup="menu"
            disabled={disabled || loading}
            onClick={handleOpen}
            data-testid={testId}
            sx={triggerIconSx}
          >
            {loading ? (
              <CircularProgress color="info" size={20} sx={{ width: '1.25rem', height: '1.25rem' }} />
            ) : (
              <PlaylistModeIcon icon={currentDisplay.Icon} color={disabled ? 'disabled' : currentColor} />
            )}
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {PLAYLIST_NEXT_SHOW_MODES.map((mode) => {
          const { Icon, tooltipKey } = PLAYLIST_NEXT_SHOW_MODE_DISPLAY[mode];
          const iconColor = getPlaylistNextShowModeIconColor(mode);

          return (
            <MenuItem
              key={mode}
              selected={mode === value}
              disabled={disabled}
              onClick={(event) => handleSelect(event, mode)}
              data-testid={testId ? `${testId}-option-${mode}` : undefined}
            >
              <ListItemIcon>
                <Box sx={{ display: 'flex', alignItems: 'center', width: 24, justifyContent: 'center' }}>
                  <PlaylistModeIcon icon={Icon} color={iconColor} />
                </Box>
              </ListItemIcon>
              <ListItemText primary={t(tooltipKey as TranslationKey)} />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
